import { ActionIcon, Anchor, Badge, Card, Menu, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconDots, IconHistory, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import describeDiscount from '../../helpers/describeDiscount';
import { generatedCouponStatus } from '../../helpers/discountConfig';
import { generatedCouponLabels } from '../../helpers/platformLabel';
import useAllowedActions from '../../helpers/useAllowedActions';
import useFetch from '../../helpers/useFetch';
import { setRequestedActionToUnlock, setSuggestSubscriptionModal } from '../../store/subscriptionSlice';
import ConfirmationModal from '../Modals/ConfirmationModal';
import AddCodesModal from './AddCodesModal';

const formatDate = (isoDate) => (isoDate ? new Date(isoDate).toLocaleString() : '-');

const StatusBadge = ({ coupon }) => {
  const status = generatedCouponStatus(coupon);
  if (status === 'error') {
    return (
      <Tooltip label={coupon.generationError} multiline w={300} withArrow>
        <Badge color="red" style={{ cursor: 'help' }}>
          Error
        </Badge>
      </Tooltip>
    );
  }
  if (status === 'generating') {
    return <Badge color="blue">Generating</Badge>;
  }
  return <Badge color="green">Live</Badge>;
};

const GeneratedCouponsTable = ({ coupons, refresh }) => {
  const { accountId, domainId } = useParams();
  const navigate = useNavigate();
  const fetch = useFetch();
  const dispatch = useDispatch();
  const domain = useSelector((state) => state.domain.domain);
  const user = useSelector((state) => state.auth.user);
  const currency = domain?.currency;
  const labels = generatedCouponLabels(domain?.platform);
  const allowedActions = useAllowedActions(domain);
  const isSuperAdmin = user.userRoles.some((role) => role.role === 'superAdmin');
  const canUnlock = isSuperAdmin || domain.account?.canManageSubscriptions;

  const isRestricted = (restrictionKey) =>
    Boolean(allowedActions) && !allowedActions.has(restrictionKey) && !allowedActions.has('*') && !isSuperAdmin;

  const gated = (restrictionKey, action) => () => {
    if (!isRestricted(restrictionKey)) {
      action();
    } else if (canUnlock) {
      dispatch(setSuggestSubscriptionModal(true));
      dispatch(setRequestedActionToUnlock(restrictionKey));
    }
  };

  const [couponAddingCodes, setCouponAddingCodes] = useState(null);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState();

  const couponsPath = `/accounts/${accountId}/domains/${domainId}/coupons`;

  const closeDeleteConfirmation = () => {
    setCouponToDelete(null);
    setDeleteError(undefined);
  };

  const deleteCoupon = () => {
    setDeleting(true);
    setDeleteError(undefined);
    fetch(`/api/admin${couponsPath}/${couponToDelete.id}`, { method: 'DELETE' })
      .then(() => {
        closeDeleteConfirmation();
        refresh?.();
      })
      .catch(setDeleteError)
      .finally(() => setDeleting(false));
  };

  return (
    <>
      <Card withBorder padding="md">
        <Table highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Coupon</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Last Updated</Table.Th>
              <Table.Th>Available/Total</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th aria-label="Actions" />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {coupons.map((coupon) => (
              <Table.Tr key={coupon.id}>
                <Table.Td>
                  <Stack gap={2}>
                    <Anchor href={`#${couponsPath}/${coupon.id}/details`} fw={600}>
                      {coupon.name}
                    </Anchor>
                    <Text size="sm" c="dimmed" lineClamp={1}>
                      {describeDiscount(coupon.discount, { currency })}
                    </Text>
                  </Stack>
                </Table.Td>
                <Table.Td>{formatDate(coupon.createdAt)}</Table.Td>
                <Table.Td>{formatDate(coupon.updatedAt)}</Table.Td>
                <Table.Td>
                  {coupon.totalCount ?? 0} / {coupon.codesTotal ?? 0}
                </Table.Td>
                <Table.Td>
                  <StatusBadge coupon={coupon} />
                </Table.Td>
                <Table.Td>
                  <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray" aria-label={`Actions for ${coupon.name}`}>
                        <IconDots size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconPlus size={14} />}
                        disabled={isRestricted('couponsUpdate') && !canUnlock}
                        onClick={gated('couponsUpdate', () => setCouponAddingCodes(coupon))}
                      >
                        Add Codes
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconHistory size={14} />}
                        onClick={() => navigate(`${couponsPath}/${coupon.id}/history`)}
                      >
                        View History
                      </Menu.Item>
                      <Menu.Item
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        disabled={isRestricted('couponsDelete') && !canUnlock}
                        onClick={gated('couponsDelete', () => setCouponToDelete(coupon))}
                      >
                        Delete
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
      <AddCodesModal coupon={couponAddingCodes} onClose={() => setCouponAddingCodes(null)} onAdded={refresh} />
      <ConfirmationModal
        show={Boolean(couponToDelete)}
        close={closeDeleteConfirmation}
        confirm={deleteCoupon}
        loading={deleting}
        error={deleteError}
        message={`Delete ${couponToDelete?.name || 'this coupon'}? Codes not sent yet are removed from ${labels.store}. This action cannot be undone!`}
      />
    </>
  );
};

export default GeneratedCouponsTable;
